import axios from "axios";
import { getApiBase } from "../apiBase";
import { isCvinExpansion } from "../projectGridColumns";
import {
  appendCommercialChangeLogEntries,
  buildCommercialPatchBody,
  listCommercialFieldChanges,
  readCommercialDomainsForRow,
} from "./commercialProjectAcf";
import { COMMERCIAL_DOMAINS, normalizeMulti } from "./commercialProjectSchema";
import {
  daysFromStartAndEnd,
  durationDaysBetween,
  formatScheduleDateLabel,
  readConstructionDaysValue,
  readIncludeWeekends,
  durationDaysLabel,
} from "./constructionScheduleDates";
import { buildExpansionPatchBody, readExpansionEditDomains } from "./expansionProjectForms";

/**
 * Merge schedule domains from the calendar editor onto expansion edit domains
 * so construction / fiber dates persist without wiping expansion summary data.
 */
function mergeScheduleDomainsForExpansion(projectRow, scheduleDomains) {
  const base = readExpansionEditDomains(projectRow, projectRow?.acf_domains);
  return {
    ...base,
    [COMMERCIAL_DOMAINS.CONSTRUCTION]: {
      ...(base[COMMERCIAL_DOMAINS.CONSTRUCTION] || {}),
      ...(scheduleDomains?.[COMMERCIAL_DOMAINS.CONSTRUCTION] || {}),
    },
    [COMMERCIAL_DOMAINS.FIBER]: {
      ...(base[COMMERCIAL_DOMAINS.FIBER] || {}),
      ...(scheduleDomains?.[COMMERCIAL_DOMAINS.FIBER] || {}),
    },
  };
}

/**
 * PATCH commercial domain fields (construction / fiber scheduling, etc.).
 * Expansion work orders use the expansion patch body so cvin_expansion stays true.
 */
export async function patchCommercialDomains({
  projectId,
  projectRow,
  domains,
  getAuthHeaders,
  apiUrl,
  source = "commercial_landing",
  userEmail = "",
  changeLogDomains = null,
}) {
  const existingAcf = projectRow?.acf_groups_json || {};
  let acfForPatch = existingAcf;
  const expansion = isCvinExpansion(projectRow);
  if (source === "schedule_calendar" && userEmail) {
    const savedDomains = domainsFromProjectRow(projectRow, projectRow?.acf_domains);
    const changes = listCommercialFieldChanges(savedDomains, domains, {
      restrictDomains: changeLogDomains,
    });
    if (changes.length > 0) {
      acfForPatch = appendCommercialChangeLogEntries(existingAcf, changes, userEmail);
    }
  }
  const body = expansion
    ? {
        ...buildExpansionPatchBody(acfForPatch, mergeScheduleDomainsForExpansion(projectRow, domains)),
        _source: source || "expansion_landing",
      }
    : buildCommercialPatchBody(acfForPatch, domains, { source });
  const base = apiUrl || getApiBase();
  const { data } = await axios.patch(`${base}/projects/${projectId}`, body, {
    headers: getAuthHeaders(),
  });
  return data;
}

export function mergeDomainFields(domains, domainKey, patch) {
  return {
    ...domains,
    [domainKey]: {
      ...(domains[domainKey] || {}),
      ...patch,
    },
  };
}

export function readConstructionSchedule(domains) {
  const c = domains?.[COMMERCIAL_DOMAINS.CONSTRUCTION] || {};
  const start = c.construction_start_date || "";
  const end = c.construction_end_date || "";
  const includeWeekends = readIncludeWeekends(c.construction_include_weekends);
  let days = readConstructionDaysValue(c.construction_days_to_complete);
  if (days === "" && start && end) {
    days = daysFromStartAndEnd(start, end, includeWeekends) ?? "";
  }
  return {
    construction_start_date: start,
    construction_end_date: end,
    construction_days_to_complete: days,
    construction_include_weekends: includeWeekends,
    construction_actual_start_date: c.construction_actual_start_date || "",
    construction_actual_end_date: c.construction_actual_end_date || "",
    construction_laborers: normalizeMulti(c.construction_laborers),
    construction_foremen: normalizeMulti(c.construction_foremen),
  };
}

/** Tooltip / calendar detail for assigned construction crew. */
export function formatConstructionCrewDetail(schedule) {
  const parts = [];
  const laborers = normalizeMulti(schedule?.construction_laborers);
  const constructionLeads = normalizeMulti(schedule?.construction_foremen);
  if (laborers.length) parts.push(`Laborers: ${laborers.join(", ")}`);
  if (constructionLeads.length) parts.push(`Construction leads: ${constructionLeads.join(", ")}`);
  return parts.join(" · ");
}

function formatDateRangeWithDays(startStr, endStr, daysOverride, includeWeekends) {
  if (!startStr && !endStr) return "";
  const startLabel = startStr ? formatScheduleDateLabel(startStr) : "—";
  const endLabel = endStr ? formatScheduleDateLabel(endStr) : "—";
  const dayCount =
    daysOverride !== "" && daysOverride != null
      ? daysOverride
      : durationDaysBetween(startStr, endStr, includeWeekends);
  const daysSuffix =
    dayCount != null
      ? includeWeekends
        ? ` (${dayCount}d)`
        : ` (${dayCount} wd)`
      : "";
  return `${startLabel} – ${endLabel}${daysSuffix}`;
}

/** Calendar tooltip: crew, planned window, and actual window when recorded. */
export function formatConstructionScheduleDetail(schedule) {
  const parts = [];
  const crew = formatConstructionCrewDetail(schedule);
  if (crew) parts.push(crew);

  const includeWeekends = readIncludeWeekends(schedule?.construction_include_weekends);

  const planned = formatDateRangeWithDays(
    schedule?.construction_start_date,
    schedule?.construction_end_date,
    schedule?.construction_days_to_complete,
    includeWeekends
  );
  if (planned) parts.push(`Planned: ${planned}`);

  const actual = formatDateRangeWithDays(
    schedule?.construction_actual_start_date,
    schedule?.construction_actual_end_date,
    "",
    includeWeekends
  );
  if (actual) parts.push(`Actual: ${actual}`);

  return parts.join(" · ");
}

export function readFiberSchedule(domains) {
  const f = domains?.[COMMERCIAL_DOMAINS.FIBER] || {};
  return {
    splicing_start_date: f.splicing_start_date || "",
    splicing_end_date: f.splicing_end_date || "",
    splicer: f.splicer || "",
  };
}

export function readNetworkingInstallations(domains) {
  const n = domains?.[COMMERCIAL_DOMAINS.NETWORKING] || {};
  const raw = n.installations;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    return Object.keys(raw)
      .filter((k) => /^\d+$/.test(k))
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => raw[k]);
  }
  return [];
}

/**
 * Networking calendar spans — one entry per installation row, with legacy single-date fallback.
 */
export function readNetworkingCalendarEvents(domains, acfGroupsJson) {
  const installations = readNetworkingInstallations(domains);
  const events = [];

  installations.forEach((row, installIndex) => {
    const startDate = row?.start_date != null ? String(row.start_date).trim() : "";
    const endDate = row?.end_date != null ? String(row.end_date).trim() : "";
    if (!startDate || !endDate) return;

    const detailParts = [];
    if (row.technician) detailParts.push(`Technician: ${String(row.technician).trim()}`);
    const locationLine = row.location != null ? String(row.location).trim().split(/\r?\n/)[0] : "";
    if (locationLine) detailParts.push(`Location: ${locationLine}`);
    const equipLine =
      row.equipment_notes != null ? String(row.equipment_notes).trim().split(/\r?\n/)[0] : "";
    if (equipLine) detailParts.push(`Equipment: ${equipLine}`);
    if (row.voip_installation === true) detailParts.push("VoIP");

    events.push({
      installIndex,
      startDate,
      endDate,
      detail: detailParts.join(" · "),
      isVoip: row.voip_installation === true,
    });
  });

  if (events.length === 0) {
    const legacy = readNetworkingSchedule(domains, acfGroupsJson);
    if (legacy.networking_start_date && legacy.networking_end_date) {
      events.push({
        installIndex: 0,
        startDate: legacy.networking_start_date,
        endDate: legacy.networking_end_date,
        detail: "",
        isVoip: false,
      });
    }
  }

  return events;
}

function readLegacyNetworkingDate(acfGroupsJson, groupKeys, fieldKeys) {
  if (!acfGroupsJson || typeof acfGroupsJson !== "object") return "";
  for (const gk of groupKeys) {
    const group = acfGroupsJson[gk];
    if (!group || typeof group !== "object") continue;
    for (const fk of fieldKeys) {
      const nested = group[fk];
      if (nested != null && typeof nested === "object" && nested[fk] != null) {
        const v = String(nested[fk]).trim();
        if (v) return v;
      }
      if (nested != null && String(nested).trim() !== "") {
        return String(nested).trim();
      }
    }
  }
  return "";
}

export function readNetworkingSchedule(domains, acfGroupsJson) {
  const n = domains?.[COMMERCIAL_DOMAINS.NETWORKING] || {};
  const start =
    n.networking_start_date ||
    readLegacyNetworkingDate(acfGroupsJson, ["networking_details", "Networking Test 2", "networking_test_2"], [
      "networking_start",
      "networking_start_date",
    ]);
  const end =
    n.networking_end_date ||
    readLegacyNetworkingDate(acfGroupsJson, ["networking_details", "Networking Test 2", "networking_test_2"], [
      "networking_complete",
      "networking_end_date",
      "networking_complete_date",
    ]);
  return {
    networking_start_date: start || "",
    networking_end_date: end || "",
  };
}

export function domainsFromProjectRow(projectRow, acfDomains) {
  return readCommercialDomainsForRow(projectRow, acfDomains);
}
